/**
 * SidebarProfile.tsx — Profile block rendered inside the sidebar footer.
 *
 * Shows the user's avatar, name, and role, plus a direct "Keluar" (logout)
 * button. The button is always visible — there's no dropdown or hover menu —
 * so sign-out is one click away from anywhere in the authed dashboard.
 *
 * Layout:
 *   - Expanded mode: avatar + name (single-line, ellipsis) + role label
 *     stacked above a full-width "Keluar" button.
 *   - Compact mode (collapsed sidebar rail): centered avatar with tooltip,
 *     plus a centered logout icon button below it.
 *
 * The name is intentionally truncated to a single line so long names like
 * "Pak Budi Santoso, S.Pd." don't push the role label out of view or wrap
 * to a second line. The full name lives in the email-derived identity so
 * it's recoverable via the title attribute.
 */

import type { JSX } from 'react';
import type { PortalContext, ShellUser } from '../../lib/navigation.js';
import { NavIcon } from './NavIcon.js';

export interface SidebarProfileProps {
  user: ShellUser;
  context: PortalContext;
  /** Compact mode hides the name + role label and shows only the avatar
   *  with the logout icon button. Used inside the collapsed (64px) rail. */
  compact?: boolean;
}

function roleLabel(user: ShellUser): string {
  if (user.role === 'admin') return 'Admin';
  if (user.role === 'tutor') return user.isApprovedTutor ? 'Tutor' : 'Calon tutor';
  if (user.kind === 'parent') return 'Orang tua';
  if (user.kind === 'student') return 'Siswa';
  return 'Pengguna';
}

async function performLogout(): Promise<void> {
  try {
    await fetch('/api/auth/logout', {
      method: 'POST',
      credentials: 'include',
    });
  } catch {
    // Ignore network errors — we still want to clear the local session and
    // redirect home so the user is not stuck on a broken state.
  }
  window.location.assign('/');
}

export function SidebarProfile({ user, compact = false }: SidebarProfileProps): JSX.Element {
  const initial = user.name?.charAt(0).toUpperCase() ?? '?';
  const label = roleLabel(user);

  if (compact) {
    return (
      <div className="sidebar-profile sidebar-profile--compact" data-testid="sidebar-profile">
        <span
          className="sidebar-profile-avatar"
          aria-hidden="true"
          title={`${user.name} • ${label}`}
        >
          {initial}
        </span>
        <button
          type="button"
          className="sidebar-profile-logout sidebar-profile-logout--icon"
          onClick={() => {
            void performLogout();
          }}
          aria-label="Keluar"
          title="Keluar"
          data-testid="sidebar-logout"
        >
          <NavIcon icon="logout" size={18} />
        </button>
      </div>
    );
  }

  return (
    <div className="sidebar-profile" data-testid="sidebar-profile">
      <div className="sidebar-profile-identity">
        <span className="sidebar-profile-avatar" aria-hidden="true">
          {initial}
        </span>
        <div className="sidebar-profile-meta">
          <span className="sidebar-profile-name" title={user.name}>
            {user.name}
          </span>
          <span className="sidebar-profile-role">{label}</span>
        </div>
      </div>
      <button
        type="button"
        className="sidebar-profile-logout"
        onClick={() => {
          void performLogout();
        }}
        data-testid="sidebar-logout"
      >
        <NavIcon icon="logout" size={16} />
        <span>Keluar</span>
      </button>
    </div>
  );
}
